import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Building2, Users, Award, Sparkles } from "lucide-react";
import { useLang } from "@/context/LanguageContext";
import { apiClient } from "@/lib/api";
import { Button } from "@/components/ui/button";

const HERO_IMG =
    "https://images.pexels.com/photos/36817668/pexels-photo-36817668.jpeg";
const FEATURE_IMG =
    "https://images.pexels.com/photos/17293003/pexels-photo-17293003.jpeg";

export default function Home() {
    const { t, lang } = useLang();
    const [stats, setStats] = useState({
        schemes: 6,
        districts: 38,
        beneficiaries_lakhs: 12,
        years_of_service: 47,
    });
    const [featured, setFeatured] = useState([]);

    useEffect(() => {
        apiClient.get("/stats").then((r) => setStats(r.data)).catch(() => {});
        apiClient
            .get("/schemes", { params: { featured_only: true } })
            .then((r) => setFeatured(r.data.slice(0, 3)))
            .catch(() => setFeatured([]));
    }, []);

    return (
        <div data-testid="home-page">
            {/* Hero */}
            <section className="relative min-h-[86vh] flex items-center overflow-hidden">
                <div className="absolute inset-0">
                    <img
                        src={HERO_IMG}
                        alt="Government of India building"
                        className="w-full h-full object-cover"
                    />
                    <div className="absolute inset-0 bg-gradient-to-r from-[#0B1A33]/95 via-[#0B1A33]/80 to-transparent" />
                </div>
                <div className="relative max-w-7xl mx-auto px-6 md:px-12 lg:px-24 py-24 grid grid-cols-1 md:grid-cols-12 gap-8 w-full">
                    <div className="md:col-span-8 text-white animate-fade-up">
                        <div className="small-caps text-[#F5B76E] mb-6">{t.home.eyebrow}</div>
                        <h1 className="font-serif font-bold text-4xl sm:text-5xl lg:text-6xl leading-[1.05] tracking-tight mb-8">
                            {t.home.heroTitle}
                        </h1>
                        <p className="text-base md:text-lg text-gray-200 max-w-2xl mb-10 leading-relaxed">
                            {t.home.heroSubtitle}
                        </p>
                        <div className="flex flex-wrap gap-4">
                            <Link to="/schemes">
                                <Button
                                    size="lg"
                                    className="bg-[#DD6B20] hover:bg-[#C05621] text-white px-8 h-12"
                                    data-testid="hero-cta-schemes"
                                >
                                    {t.home.ctaSchemes}
                                    <ArrowRight size={16} className="ml-2" />
                                </Button>
                            </Link>
                            <Link to="/contact">
                                <Button
                                    size="lg"
                                    variant="outline"
                                    className="bg-transparent border-white text-white hover:bg-white hover:text-[#1A365D] h-12 px-8"
                                    data-testid="hero-cta-contact"
                                >
                                    {t.home.ctaContact}
                                </Button>
                            </Link>
                        </div>
                    </div>
                </div>
            </section>

            {/* Stats Bento */}
            <section className="py-16 md:py-24 bg-[#F7FAFC]">
                <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
                    <div className="mb-12 max-w-3xl">
                        <div className="small-caps text-gray-500 mb-3">
                            {t.home.statsTitle}
                        </div>
                        <h2 className="font-serif font-semibold text-3xl md:text-4xl text-[#1A365D]">
                            {t.home.missionTitle}
                        </h2>
                    </div>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                        <StatCard
                            testid="stat-schemes"
                            value={stats.schemes}
                            label={t.home.statSchemes}
                            icon={<Award size={22} />}
                        />
                        <StatCard
                            testid="stat-districts"
                            value={stats.districts}
                            label={t.home.statDistricts}
                            icon={<Building2 size={22} />}
                        />
                        <StatCard
                            testid="stat-beneficiaries"
                            value={`${stats.beneficiaries_lakhs}+`}
                            label={t.home.statBeneficiaries}
                            icon={<Users size={22} />}
                        />
                        <StatCard
                            testid="stat-years"
                            value={stats.years_of_service}
                            label={t.home.statYears}
                            icon={<Sparkles size={22} />}
                        />
                    </div>
                </div>
            </section>

            {/* Mission */}
            <section className="py-16 md:py-24 bg-white">
                <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24 grid grid-cols-1 md:grid-cols-12 gap-12 items-center">
                    <div className="md:col-span-6">
                        <div className="small-caps text-[#DD6B20] mb-3">
                            {t.home.missionEyebrow}
                        </div>
                        <h2 className="font-serif font-semibold text-3xl md:text-4xl text-[#1A365D] mb-6 leading-tight">
                            {t.home.missionTitle}
                        </h2>
                        <p className="text-base leading-relaxed text-gray-700 mb-6">
                            {t.home.missionBody}
                        </p>
                        <Link to="/department">
                            <Button
                                variant="link"
                                className="text-[#1A365D] hover:text-[#DD6B20] p-0"
                                data-testid="mission-more-btn"
                            >
                                {lang === "en" ? "Read more about DIC" : "DIC பற்றி மேலும் படிக்க"}
                                <ArrowRight size={14} className="ml-2" />
                            </Button>
                        </Link>
                    </div>
                    <div className="md:col-span-6">
                        <div className="relative">
                            <img
                                src={FEATURE_IMG}
                                alt="Indian small business owner"
                                className="w-full h-[420px] object-cover rounded-lg"
                            />
                            <div className="absolute -bottom-4 -right-4 md:bottom-6 md:right-6 bg-[#DD6B20] text-white p-6 max-w-[220px] rounded-lg">
                                <div className="font-serif text-3xl font-bold leading-none">38</div>
                                <div className="small-caps mt-2 text-white/90">
                                    {lang === "en"
                                        ? "Districts. One Mission."
                                        : "38 மாவட்டங்கள். ஒரே நோக்கம்."}
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* Featured Schemes */}
            <section className="py-16 md:py-24 bg-[#F7FAFC]">
                <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
                    <div className="flex flex-col md:flex-row md:items-end md:justify-between mb-12 gap-4">
                        <div className="max-w-2xl">
                            <div className="small-caps text-gray-500 mb-3">
                                {t.home.featuredEyebrow}
                            </div>
                            <h2 className="font-serif font-semibold text-3xl md:text-4xl text-[#1A365D] mb-3">
                                {t.home.featuredTitle}
                            </h2>
                            <p className="text-gray-600">{t.home.featuredSub}</p>
                        </div>
                        <Link
                            to="/schemes"
                            className="text-sm font-semibold text-[#1A365D] hover:text-[#DD6B20] flex items-center gap-2"
                            data-testid="home-view-all-schemes"
                        >
                            {t.home.viewAll} <ArrowRight size={14} />
                        </Link>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                        {featured.map((s) => (
                            <Link
                                key={s.id}
                                to={`/schemes/${s.id}`}
                                className="group bg-white border border-gray-200 p-8 rounded-lg hover:border-[#1A365D] hover:shadow-lg transition-all"
                                data-testid={`featured-scheme-${s.id}`}
                            >
                                <div className="small-caps text-[#DD6B20] mb-4">
                                    {t.schemes.categories[s.category] || s.category}
                                </div>
                                <h3 className="font-serif font-semibold text-xl text-[#1A365D] mb-3 leading-snug min-h-[56px]">
                                    {lang === "en" ? s.name_en : s.name_ta}
                                </h3>
                                <p className="text-sm text-gray-600 leading-relaxed mb-6 line-clamp-3">
                                    {lang === "en" ? s.short_desc_en : s.short_desc_ta}
                                </p>
                                <div className="text-sm text-[#1A365D] font-semibold flex items-center gap-2 group-hover:gap-3 transition-all">
                                    {t.schemes.viewDetails}
                                    <ArrowRight size={14} />
                                </div>
                            </Link>
                        ))}
                    </div>
                </div>
            </section>
        </div>
    );
}

function StatCard({ value, label, icon, testid }) {
    return (
        <div
            className="bg-white border border-gray-200 p-6 md:p-8 rounded-lg hover:border-[#1A365D] transition-colors"
            data-testid={testid}
        >
            <div className="text-[#DD6B20] mb-4">{icon}</div>
            <div className="font-serif text-3xl md:text-4xl font-bold text-[#1A365D] leading-none">
                {value}
            </div>
            <div className="small-caps text-gray-500 mt-3">{label}</div>
        </div>
    );
}
